import { useEffect, useRef, useState } from "react";
import ChatHeader from "./components/ChatHeader";
import ChatMessage from "./components/ChatMessage";
import ChatInput from "./components/ChatInput";
import DefaultQuestions from "./components/DefaultQuestions";
import StaticPrompts from "./components/StaticPrompts";
import TypingIndicator from "./components/TypingIndicator";
import ContactButtons from "./components/ContactButtons";
import { cleanCitationMarkers } from "./utils/messageUtils";
import useUser from "context/hooks/user";
import { useAppSelector } from "store/hooks";
import { getSettings } from "store/slices";
import {
  getChatById,
  addMessage,
  getChatSettings,
} from "./services/chatService";
import { t } from "i18next";
import { showToast } from "utils/toast";

export const contactButtonsVisibility = [
  "don't have information",
  "I'm sorry",
  "does not contain",
];
export default function Chatbot({ onClose }) {
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [tokenLimit, setTokenLimit] = useState(0);
  const [tokenUsage, setTokenUsage] = useState(0);
  const [defaultQuestions, setDefaultQuestions] = useState([]);
  const [commonQuestions, setCommonQuestions] = useState([]);
  const { data: userDetail } = useUser();
  const { tenant: tenantDetail } = useAppSelector(getSettings);

  // Load user's chats and user info
  useEffect(() => {
    if (!userDetail._id || !tenantDetail?.institution_title) return;

    const loadData = async () => {
      try {
        setIsLoading(true);

        const chatSettings = await getChatSettings();
        if (chatSettings) {
          setTokenLimit(chatSettings.maximum_tokens_per_thread);
          setDefaultQuestions(chatSettings.default_four_questions ?? []);
          setCommonQuestions(chatSettings.most_common_questions ?? []);
        }
        const chatResult = await getChatById();
        if (chatResult.messages) {
          if (chatResult.messages && chatResult.messages.length > 0) {
            setMessages(chatResult.messages);
          }
          if (chatResult.tokens_used?.input && chatResult.tokens_used?.output) {
            setTokenUsage(
              chatResult.tokens_used?.input + chatResult.tokens_used?.output
            );
          }
        }
        // }
      } catch (error) {
        console.error("Error loading chat data:", error);
        showToast(
          "Failed to load chat data. Please try again.",
          "error",
          "bottom-right",
          "destructive"
        );
        // toast({
        //   title: "Error",
        //   description: "Failed to load chat data. Please try again.",
        //   variant: "destructive",
        // });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [userDetail._id, tenantDetail?.institution_title]);

  // Automatically scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (text) => {
    // || !currentChat || isLoading || tokenLimit || !isActive
    if (!text.trim()) return;

    // Create a temporary user message to display immediately
    const tempMessage = {
      content: text,
      role: "user",
      createdAt: new Date(),
    };

    // Add message to UI immediately
    setMessages((prev) => [...prev, tempMessage]);
    setIsLoading(true);
    setIsTyping(true);

    let tempQuestion = [...commonQuestions];

    const randomizedCommonQuestion = tempQuestion.sort(
      () => Math.random() - 0.5
    );
    setCommonQuestions(randomizedCommonQuestion);

    try {
      // Send message to API
      const result = await addMessage(text);
      setIsTyping(false);

      if (result.message) {
        setMessages((prev) => [...prev, result.message]);
      }
      if (result.tokens_used) {
        setTokenUsage(result.tokens_used?.input + result.tokens_used?.output);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      showToast(
        "Failed to send message. Please try again.",
        "error",
        "bottom-right",
        "destructive"
      );
    } finally {
      setIsLoading(false);
      setIsTyping(false);
    }
  };
  // Determine if we should show the welcome message
  const showWelcome = !messages || messages.length === 0;

  if (!userDetail._id || !tenantDetail?.institution_title) {
    return (
      <div className="flex justify-center items-center h-full">
        No user or tenant specified
      </div>
    );
  }
  return (
    <>
      <div className="flex flex-col h-dvh bg-zinc-50 rounded-lg overflow-hidden">
        <ChatHeader title={`${t("chat_bot_title")}`} onClose={onClose} />

        <div className="flex-1 overflow-y-auto px-2 py-3 space-y-4">
          {isLoading && (!messages || messages.length === 0) ? (
            <div className="flex justify-center items-center h-64">
              {/* <Spinner size="lg" /> */}
            </div>
          ) : (
            showWelcome && (
              <StaticPrompts
                questions={defaultQuestions}
                handleSendMessage={handleSendMessage}
                tenantName={tenantDetail?.institution_title}
              />
            )
          )}

          {messages &&
            messages.map((message, index) => (
              <ChatMessage
                key={index}
                message={{
                  id: message._id || String(index),
                  content:
                    message.role === "user"
                      ? message.content
                      : cleanCitationMarkers(message.content),
                  sender: message.role === "user" ? "user" : "assistant",
                  timestamp: message.createdAt,
                }}
              />
            ))}

          {/* Show typing indicator when isTyping is true */}
          <TypingIndicator visible={isTyping} loaderType="ripple" />
          <div ref={messagesEndRef} />
        </div>

        <div className="px-1 py-1.5 border-t">
          {messages.length !== 0 && commonQuestions.length > 0 && (
            <DefaultQuestions
              questions={commonQuestions}
              onQuestionClick={(q) => handleSendMessage(q)}
            />
          )}
          {(messages.length > 0 &&
            messages[messages.length - 1].role === "assistant" &&
            contactButtonsVisibility.some((phrase) =>
              messages[messages.length - 1].content
                .toLowerCase()
                .includes(phrase)
            )) ||
          tokenUsage >= tokenLimit ? (
            <ContactButtons tenantDetail={tenantDetail} />
          ) : (
            ""
          )}
          {tokenUsage >= tokenLimit ? (
            <>
              <div className="text-center text-red-500 p-2 mb-2 bg-red-50 rounded">
                {t("text_when_reach_token_limit_on_chat")}
              </div>
            </>
          ) : (
            !isLoading && (
              <>
                <ChatInput
                  onSendMessage={handleSendMessage}
                  placeholder="Type your message..."
                />
              </>
            )
          )}
        </div>
      </div>
    </>
  );
}
